import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { retrySendLog } from "@/lib/telegram.functions";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, RotateCw } from "lucide-react";

export const Route = createFileRoute("/_authenticated/logs")({ component: Page });

type Range = "1h" | "24h" | "7d" | "30d" | "all";
const rangeMs: Record<Range, number | null> = { "1h": 3600e3, "24h": 86400e3, "7d": 7 * 86400e3, "30d": 30 * 86400e3, all: null };

function sinceIso(range: Range): string | null {
  const ms = rangeMs[range];
  return ms ? new Date(Date.now() - ms).toISOString() : null;
}

function Page() {
  const [destFilter, setDestFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [range, setRange] = useState<Range>("24h");
  const [retrying, setRetrying] = useState<Record<string, boolean>>({});
  const retryFn = useServerFn(retrySendLog);

  const { data: dests } = useQuery({
    queryKey: ["dests-min"],
    queryFn: async () => (await supabase.from("telegram_destinations").select("id, destination_name").order("destination_name")).data ?? [],
  });

  const tgQuery = useQuery({
    queryKey: ["tg-logs", destFilter, statusFilter, range],
    queryFn: async () => {
      let q = supabase
        .from("telegram_send_logs")
        .select("*, telegram_destinations(destination_name), news_items(original_title)")
        .order("sent_at", { ascending: false })
        .limit(500);
      const since = sinceIso(range);
      if (since) q = q.gte("sent_at", since);
      if (destFilter !== "all") q = q.eq("telegram_destination_id", destFilter);
      if (statusFilter !== "all") q = q.eq("send_status", statusFilter as "success" | "failed" | "pending");
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

  const trQuery = useQuery({
    queryKey: ["tr-logs", range],
    queryFn: async () => {
      let q = supabase
        .from("translated_news")
        .select("id, news_item_id, translation_status, translated_at, created_at, title_th, news_items(original_title, asset_categories(name_th, icon))")
        .order("translated_at", { ascending: false, nullsFirst: false })
        .limit(500);
      const since = sinceIso(range);
      if (since) q = q.gte("created_at", since);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: sys } = useQuery({
    queryKey: ["sys-logs", range],
    queryFn: async () => {
      let q = supabase.from("system_logs").select("*").order("created_at", { ascending: false }).limit(500);
      const since = sinceIso(range);
      if (since) q = q.gte("created_at", since);
      return (await q).data ?? [];
    },
  });

  const tg = tgQuery.data ?? [];
  const tr = trQuery.data ?? [];

  const tgStats = useMemo(() => {
    const ok = tg.filter((l: any) => l.send_status === "success").length;
    const fail = tg.filter((l: any) => l.send_status === "failed").length;
    const avg = tg.length ? Math.round(tg.reduce((s: number, l: any) => s + (l.duration_ms ?? 0), 0) / tg.length) : 0;
    return { total: tg.length, ok, fail, avg };
  }, [tg]);

  const trStats = useMemo(() => {
    const done = tr.filter((l: any) => l.translation_status === "done").length;
    const pending = tr.filter((l: any) => l.translation_status === "pending").length;
    const failed = tr.filter((l: any) => l.translation_status === "failed" || l.translation_status === "error").length;
    return { total: tr.length, done, pending, failed };
  }, [tr]);

  const refreshAll = () => { tgQuery.refetch(); trQuery.refetch(); };

  const doRetry = async (logId: string) => {
    setRetrying((p) => ({ ...p, [logId]: true }));
    toast.loading("กำลังลองส่งใหม่... (มี backoff)", { id: `retry-${logId}` });
    try {
      const r: any = await retryFn({ data: { logId } });
      if (r?.ok) toast.success(`ส่งสำเร็จ (attempt ${r.attempt_no}, backoff ${r.backoff_sec}s)`, { id: `retry-${logId}` });
      else toast.error(`ล้มเหลว (attempt ${r?.attempt_no}): ${r?.error ?? "unknown"}`, { id: `retry-${logId}` });
      tgQuery.refetch();
    } catch (e: any) {
      toast.error(`Retry error: ${e.message ?? e}`, { id: `retry-${logId}` });
    } finally {
      setRetrying((p) => { const n = { ...p }; delete n[logId]; return n; });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-semibold">Logs</h1>
          <p className="text-sm text-muted-foreground">บันทึกการแปลภาษาและการส่ง Telegram แบบละเอียดต่อรอบการทำงาน</p>
        </div>
        <Button variant="outline" size="sm" onClick={refreshAll}><RefreshCw className="h-3 w-3 mr-1"/>รีเฟรช</Button>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">ตัวกรอง</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">ช่วงเวลา</label>
            <Select value={range} onValueChange={(v) => setRange(v as Range)}>
              <SelectTrigger className="w-[160px]"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">1 ชั่วโมงล่าสุด</SelectItem>
                <SelectItem value="24h">24 ชั่วโมงล่าสุด</SelectItem>
                <SelectItem value="7d">7 วันล่าสุด</SelectItem>
                <SelectItem value="30d">30 วันล่าสุด</SelectItem>
                <SelectItem value="all">ทั้งหมด</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">ปลายทาง Telegram</label>
            <Select value={destFilter} onValueChange={setDestFilter}>
              <SelectTrigger className="w-[220px]"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทั้งหมด</SelectItem>
                {(dests ?? []).map((d: any) => <SelectItem key={d.id} value={d.id}>{d.destination_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">สถานะการส่ง</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทั้งหมด</SelectItem>
                <SelectItem value="success">success</SelectItem>
                <SelectItem value="failed">failed</SelectItem>
                <SelectItem value="pending">pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="tg">
        <TabsList>
          <TabsTrigger value="tg">Telegram Send ({tgStats.total})</TabsTrigger>
          <TabsTrigger value="tr">Translation ({trStats.total})</TabsTrigger>
          <TabsTrigger value="sys">System</TabsTrigger>
        </TabsList>

        <TabsContent value="tg">
          <Card>
            <CardHeader>
              <CardTitle>Telegram Send Logs</CardTitle>
              <CardDescription className="flex flex-wrap gap-3 text-xs">
                <span>ทั้งหมด {tgStats.total}</span>
                <span className="text-green-500">สำเร็จ {tgStats.ok}</span>
                <span className="text-destructive">ล้มเหลว {tgStats.fail}</span>
                <span>Avg {tgStats.avg}ms</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>เวลา</TableHead><TableHead>ปลายทาง</TableHead><TableHead>สถานะ</TableHead>
                  <TableHead>Try</TableHead><TableHead>Duration</TableHead><TableHead>ข่าว</TableHead><TableHead>Error</TableHead><TableHead className="text-right">Action</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {tg.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">ไม่มีข้อมูลในช่วงที่เลือก</TableCell></TableRow>}
                  {tg.map((l: any) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-xs whitespace-nowrap">{new Date(l.sent_at).toLocaleString("th-TH")}</TableCell>
                      <TableCell className="text-xs">{l.telegram_destinations?.destination_name ?? "—"}</TableCell>
                      <TableCell>
                        {l.send_status === "success"
                          ? <Badge className="bg-green-600 hover:bg-green-600">success</Badge>
                          : <Badge variant="destructive">{l.send_status}</Badge>}
                      </TableCell>
                      <TableCell className="text-xs">{l.attempt_no}</TableCell>
                      <TableCell className="text-xs">{l.duration_ms ?? "—"}ms</TableCell>
                      <TableCell className="text-xs max-w-[280px] truncate" title={l.news_items?.original_title ?? l.message}>
                        {l.news_items?.original_title ?? <span className="text-muted-foreground italic">ทดสอบ / ไม่มีข่าวอ้างอิง</span>}
                      </TableCell>
                      <TableCell className="text-xs text-destructive max-w-[260px] truncate" title={l.error_message ?? ""}>{l.error_message ?? "—"}</TableCell>
                      <TableCell className="text-right">
                        {l.send_status === "failed" ? (
                          <Button size="sm" variant="outline" disabled={!!retrying[l.id]} onClick={() => doRetry(l.id)}>
                            <RotateCw className={`h-3 w-3 mr-1 ${retrying[l.id] ? "animate-spin" : ""}`}/>
                            {retrying[l.id] ? "กำลังลอง..." : "Retry"}
                          </Button>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tr">
          <Card>
            <CardHeader>
              <CardTitle>Translation Logs</CardTitle>
              <CardDescription className="flex flex-wrap gap-3 text-xs">
                <span>ทั้งหมด {trStats.total}</span>
                <span className="text-green-500">เสร็จ {trStats.done}</span>
                <span>กำลังแปล {trStats.pending}</span>
                <span className="text-destructive">ล้มเหลว {trStats.failed}</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>เวลา</TableHead><TableHead>หมวด</TableHead><TableHead>สถานะ</TableHead>
                  <TableHead>หัวข้อต้นฉบับ</TableHead><TableHead>หัวข้อแปล</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {tr.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">ไม่มีข้อมูลในช่วงที่เลือก</TableCell></TableRow>}
                  {tr.map((l: any) => {
                    const cat = l.news_items?.asset_categories;
                    return (
                      <TableRow key={l.id}>
                        <TableCell className="text-xs whitespace-nowrap">{new Date(l.translated_at ?? l.created_at).toLocaleString("th-TH")}</TableCell>
                        <TableCell className="text-xs">{cat ? `${cat.icon ?? "📰"} ${cat.name_th}` : "—"}</TableCell>
                        <TableCell>
                          {l.translation_status === "done"
                            ? <Badge className="bg-green-600 hover:bg-green-600">done</Badge>
                            : l.translation_status === "pending"
                              ? <Badge variant="secondary">pending</Badge>
                              : <Badge variant="destructive">{l.translation_status}</Badge>}
                        </TableCell>
                        <TableCell className="text-xs max-w-[300px] truncate" title={l.news_items?.original_title ?? ""}>{l.news_items?.original_title ?? "—"}</TableCell>
                        <TableCell className="text-xs max-w-[300px] truncate" title={l.title_th ?? ""}>{l.title_th ?? "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sys">
          <Card>
            <CardHeader><CardTitle>System Logs</CardTitle><CardDescription>{sys?.length ?? 0} รายการ</CardDescription></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>เวลา</TableHead><TableHead>ประเภท</TableHead><TableHead>ข้อความ</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(sys ?? []).map((l: any) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString("th-TH")}</TableCell>
                      <TableCell><Badge variant="outline">{l.log_type}</Badge></TableCell>
                      <TableCell className="text-xs">{l.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
